import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import config from '@/config';
import ProfileDataContext from '@/context/ProfileDataContext';
import useCache from '@/hooks/useCache';
import useStateHandler from '@/hooks/useStateHandler';
import useTranslations from '@/i18n/useTranslations';
import GalleryService from '@/services/gallery.service';
import GenericPopup from '@/shared-components/popups/components/GenericPopup';
import Text from '@/shared-components/text/components/Text';

import QueuedArtworksPopup from './QueuedArtworksPopup';

import './styles/PopUpCreateOptions.css';

const PopUpCreateOptions = ({
  open,
  close,
  openDraftPopup,
  textOption,
  to,
  requireDraftsForOpen = false,
  enableQueue = true,
}) => {
  const navigate = useNavigate();
  const t = useTranslations('common.profile.popups.create_options');
  const { cacheHandler, stateHandler } = useStateHandler();
  const { profileData, setProfileData } = useContext(ProfileDataContext);
  const profileId = profileData?._id;
  const draftCacheKey = profileId ? `${profileId}_artwork_drafts` : 'artwork_drafts';
  const queuedArtworksCacheKey = profileId ? `${profileId}_queued_artworks` : 'queued_artworks';
  const invalidateKeys = [
    'ARTWORK_DRAFT_SAVED',
    'NEW_ARTWORK_REQUEST',
    profileId ? `${profileId}_REFRESH` : null
  ].filter(Boolean);
  const [queuedPopupOpen, setQueuedPopupOpen] = useState(false);

  const { loading: loadingDrafts, fetchData, setFetchData } = useCache(
    draftCacheKey,
    `${config.apis.api.url}/drafts`,
    {
      injectToken: true,
      invalidateWhen: invalidateKeys
    }
  );
  
  // NEW: fetch queued artworks (already accepted, waiting for publish time)
  const queueDisabled = !enableQueue || !profileId;
  const {
    loading: loadingQueuedArtworks,
    fetchData: queuedArtworksData,
    setFetchData: setQueuedArtworksData
  } = useCache(
    queuedArtworksCacheKey,
    // Private endpoint that returns this gallery's artworks; must include queued
    `${config.apis.api.url}/gallery/${profileId}/artworks?status=queued`,
    { injectToken: true, invalidateWhen: invalidateKeys, disabled: queueDisabled }
  );

  // When profileId is undefined (rare), avoid rendering queue content.
  const draftsData = fetchData?.data ?? [];
  const artworksData = queuedArtworksData?.data ?? [];

  const queuedDrafts = useMemo(() => {
    return draftsData.filter(item => {
      const status = (item?.draft_status || '').toString().toLowerCase();
      return status === 'queued';
    });
  }, [draftsData]);

  const queuedArtworks = useMemo(() => {
    return artworksData.filter(item => {
      const status = (item?.artwork_status || '').toString().toLowerCase();
      return status === 'queued';
    });
  }, [artworksData]);

  const totalQueued = queuedDrafts.length + queuedArtworks.length;
  const shouldShowQueueOption = !queueDisabled && ((!loadingDrafts && queuedDrafts.length > 0)
    || (!loadingQueuedArtworks && queuedArtworks.length > 0));
  const shouldShowOpenDrafts = requireDraftsForOpen ? draftsData.length > 0 : true;

  const handleClosePopup = useCallback((options = {}) => {
    if (!options.keepQueueOpen) {
      setQueuedPopupOpen(false);
    }

    if (typeof close === 'function') {
      close();
    }
  }, [close]);

  useEffect(() => {
    if (queuedPopupOpen && totalQueued === 0) {
      setQueuedPopupOpen(false);
    }
  }, [queuedPopupOpen, totalQueued]);

  useEffect(() => {
    if (!open) {
      return;
    }

    setQueuedPopupOpen(false);
  }, [open]);

  const handleNavigateToDraft = draft => {
    navigate(to, {
      state: {
        from: true,
        draftData: draft
      }
    });
    handleClosePopup();
  };

  // NEW: navigate to queued artwork (detail or inventory page)
  const handleNavigateToArtwork = artwork => {
    navigate(`/artwork/${artwork.slug || artwork._id}`);
    handleClosePopup();
  };

  const handleRemoveQueuedDraft = useCallback(async draft => {
    if (!draft?._id) return;

    const draftId = draft._id;
    setFetchData(prev => {
      const base = prev ?? { data: [], totalDocs: 0 };
      return {
        ...base,
        totalDocs: Math.max((base.totalDocs ?? 1) - 1, 0),
        data: (base.data ?? []).filter(item => item._id !== draftId)
      };
    });

    setProfileData(prev => ({
      ...prev,
      draft_count: Math.max((prev?.draft_count ?? 1) - 1, 0)
    }));

    await GalleryService.removeArtworkDraft(draftId);
    cacheHandler.triggerAction('ARTWORK_DRAFT_SAVED');
    stateHandler.set('temporalPopup', {
      text: t('messages.queued_artwork_removed'),
      type: 'navbar-present'
    });
  }, [cacheHandler, setFetchData, setProfileData, stateHandler]);

  const handleRemoveQueuedArtwork = useCallback(async artwork => {
    if (!artwork?._id) return;

    const artworkId = artwork._id;

    try {
      await GalleryService.deleteArtwork(artworkId);

      setQueuedArtworksData(prev => {
        const base = prev ?? { data: [], totalDocs: 0 };
        return {
          ...base,
          totalDocs: Math.max((base.totalDocs ?? 1) - 1, 0),
          data: (base.data ?? []).filter(item => item._id !== artworkId)
        };
      });

      cacheHandler.triggerAction('DELETE_ARTWORK');
      stateHandler.set('temporalPopup', {
        text: t('messages.queued_artwork_removed'),
        type: 'navbar-present'
      });
    } catch (error) {
      console.error('Failed to remove queued artwork', error);
      throw error;
    }
  }, [cacheHandler, setQueuedArtworksData, stateHandler]);

  return (
    <>
      <GenericPopup
        open={open}
        className="remove-scrollbar"
        backgroundClassName="pop-up-artwork-create__backdrop"
      >
        <div className="pop-up-artwork-create" onClick={() => handleClosePopup()}>
          <div
            className="pop-up-artwork-create-container"
            onClick={event => event.stopPropagation()}
          >
            <button
              type="button"
              className="pop-up-artwork-create-options"
              onClick={() => {
                navigate(to, { state: { from: true } });
                handleClosePopup();
              }}
            >
              <Text>{t('options.new_item', { item: textOption })}</Text>
            </button>

            {shouldShowOpenDrafts && (
              <button
                type="button"
                className="pop-up-artwork-create-options"
                onClick={() => {
                  handleClosePopup();
                  openDraftPopup(true);
                }}
              >
                <Text>{t('options.open_drafts')}</Text>
              </button>
            )}

            {shouldShowQueueOption && totalQueued > 0 && (
              <button
                type="button"
                className="pop-up-artwork-create-options"
                onClick={event => {
                  event.stopPropagation();
                  setQueuedPopupOpen(true);
                  handleClosePopup({ keepQueueOpen: true });
                }}
              >
                <Text>{t('options.queued_count', { count: totalQueued })}</Text>
              </button>
            )}
          </div>
        </div>
      </GenericPopup>

      <QueuedArtworksPopup
        open={queuedPopupOpen}
        onClose={() => setQueuedPopupOpen(false)}
        drafts={queuedDrafts}
        artworks={queuedArtworks}
        onDraftOpen={handleNavigateToDraft}
        onArtworkOpen={handleNavigateToArtwork}
        onDraftDelete={handleRemoveQueuedDraft}
        onArtworkDelete={handleRemoveQueuedArtwork}
      />
    </>
  );
};

export default PopUpCreateOptions;
